/**
 * Background Sync Orchestrator
 * 
 * Coordinates offline action syncing with server
 * Handles retry logic, conflict resolution, and error recovery
 */

import { apiFetch } from '@/api/core/apiFetch';
import { NetworkError, isNetworkError } from '@/api/core/errors';
import {
  OFFLINE_REPLAY_HEADER,
  OFFLINE_SYNC_COMPLETED_EVENT,
  readQueuedApiRequest,
} from './offlineRequestQueue';
import { 
  getPendingActions, 
  updateAction, 
  removeAction, 
  type QueuedAction,
  type ActionType 
} from './offlineQueue';

const MAX_RETRIES = 3;
const RETRY_DELAY = 2000; // 2 seconds

// Extend ServiceWorkerRegistration to include the experimental Background Sync API
interface SyncManager {
  register(tag: string): Promise<void>;
  getTags(): Promise<string[]>;
}

interface ServiceWorkerRegistrationWithSync extends ServiceWorkerRegistration {
  sync: SyncManager;
}

export interface SyncResult {
  success: boolean;
  synced: number;
  failed: number;
  errors: Array<{ actionId: string; error: string }>;
  /**
   * true quando il replay si è interrotto perché la rete è ancora assente:
   * le azioni restano `pending` (nessun tentativo consumato) e verranno
   * rigiocate al prossimo ritorno della connessione.
   */
  stoppedOffline?: boolean;
}

/**
 * Un fallimento di connettività NON deve consumare i tentativi dell'azione:
 * altrimenti tre tentativi fatti mentre la rete è giù marcano come `failed`
 * per sempre un dato dell'utente che non ha mai raggiunto il server.
 */
function isConnectivityFailure(error: unknown): boolean {
  return error instanceof NetworkError || isNetworkError(error);
}

/**
 * Path API toccato da un'azione in coda (usato per invalidare le cache a fine
 * replay). Ritorna null per le azioni non riconducibili a una risorsa.
 */
function actionApiPath(action: QueuedAction): string | null {
  const data = (action.data ?? {}) as Record<string, any>;
  switch (action.type) {
    case 'create-event':
      return '/api/events';
    case 'join-event':
      return data.eventDateId ? `/api/events/${data.eventDateId}/join` : '/api/events';
    case 'add-bike':
      return '/api/garage/bikes';
    case 'update-bike':
    case 'delete-bike':
      return data.bikeId ? `/api/garage/bikes/${data.bikeId}` : '/api/garage';
    case 'update-profile':
      return '/api/profile/me';
    case 'upload-photo':
      return typeof data.endpoint === 'string' ? data.endpoint : null;
    case 'api-request':
      return readQueuedApiRequest(action)?.path ?? null;
    default:
      return null;
  }
}

/** Avvisa chi legge da cache che il server è cambiato (ponte `offlineCacheBridge`). */
function emitSyncCompleted(paths: string[]): void {
  if (paths.length === 0 || typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent<{ paths: string[] }>(OFFLINE_SYNC_COMPLETED_EVENT, {
      detail: { paths },
    }),
  );
}

/**
 * Sync all pending actions
 */
export async function syncPendingActions(): Promise<SyncResult> {
  const actions = await getPendingActions();
  
  const result: SyncResult = {
    success: true,
    synced: 0,
    failed: 0,
    errors: [],
  };

  // Risorse scritte sul server durante QUESTO replay: a fine giro le cache
  // (locale Dexie + query attive) vengono invalidate una volta sola.
  const syncedPaths: string[] = [];

  for (const action of actions) {
    try {
      await syncAction(action);
      await removeAction(action.id);
      const path = actionApiPath(action);
      if (path && !syncedPaths.includes(path)) syncedPaths.push(path);
      result.synced++;
      console.log(`[BackgroundSync] ✅ Synced: ${action.type}`);
    } catch (error: any) {
      // Rete ancora assente: l'azione torna `pending` senza consumare un
      // tentativo, e il replay si ferma (anche le successive fallirebbero
      // allo stesso modo). Riprenderà al prossimo evento di rete.
      if (isConnectivityFailure(error)) {
        await updateAction(action.id, { status: 'pending' });
        result.success = false;
        result.stoppedOffline = true;
        result.errors.push({
          actionId: action.id,
          error: error?.message ?? 'Rete non disponibile',
        });
        console.warn(`[BackgroundSync] ⏸️ Rete assente, replay sospeso: ${action.type}`);
        break;
      }

      result.failed++;
      result.success = false;
      result.errors.push({
        actionId: action.id,
        error: error.message,
      });

      // Update retry count
      const newRetries = action.retries + 1;
      
      if (newRetries >= MAX_RETRIES) {
        await updateAction(action.id, {
          status: 'failed',
          retries: newRetries,
          error: error.message,
        });
        console.error(`[BackgroundSync] ❌ Failed after ${MAX_RETRIES} retries:`, action.type);
      } else {
        await updateAction(action.id, {
          retries: newRetries,
          error: error.message,
        });
        console.warn(`[BackgroundSync] ⚠️ Retry ${newRetries}/${MAX_RETRIES}:`, action.type);
      }
    }
  }

  // Anche se il replay si è fermato (rete assente) le azioni già applicate
  // vanno annunciate: le schermate devono rileggere i dati scritti.
  emitSyncCompleted(syncedPaths);

  return result;
}

/**
 * Sync single action based on type
 */
async function syncAction(action: QueuedAction): Promise<void> {
  await updateAction(action.id, { status: 'syncing' });

  switch (action.type) {
    case 'create-event':
      return syncCreateEvent(action);
    case 'join-event':
      return syncJoinEvent(action);
    case 'add-bike':
      return syncAddBike(action);
    case 'update-bike':
      return syncUpdateBike(action);
    case 'delete-bike':
      return syncDeleteBike(action);
    case 'update-profile':
      return syncUpdateProfile(action);
    case 'upload-photo':
      return syncUploadPhoto(action);
    case 'api-request':
      return syncApiRequest(action);
    default:
      throw new Error(`Unknown action type: ${action.type}`);
  }
}

/**
 * Sync: Create Event
 */
async function syncCreateEvent(action: QueuedAction): Promise<void> {
  const data = await apiFetch<{ event?: { id: string } }>('/api/events', {
    method: 'POST',
    body: JSON.stringify(action.data),
    headers: {
      'X-Idempotency-Key': action.idempotencyKey,
      'Content-Type': 'application/json',
    },
  });

  console.log('[BackgroundSync] Event created:', data.event?.id);
}

/**
 * Sync: Join Event
 */
async function syncJoinEvent(action: QueuedAction): Promise<void> {
  const { eventDateId, bikeId, partySize } = action.data;

  await apiFetch(`/api/events/${eventDateId}/join`, {
    method: 'POST',
    body: JSON.stringify({ bike_id: bikeId, party_size: partySize }),
    headers: {
      'X-Idempotency-Key': action.idempotencyKey,
      'Content-Type': 'application/json',
    },
  });

  console.log('[BackgroundSync] Joined event:', eventDateId);
}

/**
 * Sync: Add Bike
 */
async function syncAddBike(action: QueuedAction): Promise<void> {
  const data = await apiFetch<{ bike?: { id: string } }>('/api/garage/bikes', {
    method: 'POST',
    body: JSON.stringify(action.data),
    headers: {
      'X-Idempotency-Key': action.idempotencyKey,
      'Content-Type': 'application/json',
    },
  });

  console.log('[BackgroundSync] Bike added:', data.bike?.id);
}

/**
 * Sync: Update Bike
 */
async function syncUpdateBike(action: QueuedAction): Promise<void> {
  const { bikeId, ...data } = action.data;

  // FIX OFFLINE-FLUSH (Ott 2026): il metodo corretto è PATCH — PUT non esiste
  // su /api/garage/bikes/:id (garage.ts espone PATCH), quindi il replay di una
  // modifica moto offline finiva sempre in 404.
  await apiFetch(`/api/garage/bikes/${bikeId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
    headers: {
      'X-Idempotency-Key': action.idempotencyKey,
      'Content-Type': 'application/json',
    },
  });

  console.log('[BackgroundSync] Bike updated:', bikeId);
}

/**
 * Sync: Delete Bike
 */
async function syncDeleteBike(action: QueuedAction): Promise<void> {
  const { bikeId } = action.data;

  await apiFetch(`/api/garage/bikes/${bikeId}`, {
    method: 'DELETE',
    headers: {
      'X-Idempotency-Key': action.idempotencyKey,
    },
  });

  console.log('[BackgroundSync] Bike deleted:', bikeId);
}

/**
 * Sync: Update Profile
 */
async function syncUpdateProfile(action: QueuedAction): Promise<void> {
  // FIX OFFLINE-FLUSH (Ott 2026): l'endpoint reale è /api/profile/me (PATCH).
  // Prima: PUT su /api/profile, che non esiste → ogni aggiornamento profilo
  // messo in coda offline veniva perso al primo replay.
  await apiFetch('/api/profile/me', {
    method: 'PATCH',
    body: JSON.stringify(action.data),
    headers: {
      'X-Idempotency-Key': action.idempotencyKey,
      'Content-Type': 'application/json',
    },
  });

  console.log('[BackgroundSync] Profile updated');
}

/**
 * Sync: Upload Photo
 */
async function syncUploadPhoto(action: QueuedAction): Promise<void> {
  const { endpoint, formData } = action.data;

  // NOTA: formData è un oggetto serializzato, non un FormData nativo.
  // Il server si aspetta JSON, non multipart.
  await apiFetch(endpoint, {
    method: 'POST',
    body: JSON.stringify(formData),
    headers: {
      'X-Idempotency-Key': action.idempotencyKey,
      'Content-Type': 'application/json',
    },
  });

  console.log('[BackgroundSync] Photo uploaded');
}

/**
 * Sync: richiesta HTTP generica accodata da apiFetch quando la rete è caduta.
 * L'URL è quello originale (path relativo all'API) e il replay passa da
 * apiFetch, quindi con base URL corretta e token aggiornato.
 */
async function syncApiRequest(action: QueuedAction): Promise<void> {
  const request = readQueuedApiRequest(action);
  if (!request) {
    throw new Error('Richiesta offline non valida (path mancante)');
  }

  await apiFetch(request.path, {
    method: request.method,
    body: request.body ?? undefined,
    headers: {
      'Content-Type': 'application/json',
      'X-Idempotency-Key': action.idempotencyKey,
      // Impedisce il ri-accodamento se anche il replay fallisce per rete.
      [OFFLINE_REPLAY_HEADER]: '1',
    },
  });

  console.log(`[BackgroundSync] Richiesta rigiocata: ${request.method} ${request.path}`);
}

/**
 * Register background sync (if supported)
 */
export async function registerBackgroundSync(tag: string = 'sync-actions'): Promise<boolean> {
  if (!('serviceWorker' in navigator) || !('sync' in ServiceWorkerRegistration.prototype)) {
    console.warn('[BackgroundSync] Background Sync API not supported');
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready as ServiceWorkerRegistrationWithSync;
    await registration.sync.register(tag);
    console.log(`[BackgroundSync] Registered sync: ${tag}`);
    return true;
  } catch (error) {
    console.error('[BackgroundSync] Failed to register sync:', error);
    return false;
  }
}

/**
 * Trigger immediate sync (fallback if Background Sync not supported)
 */
export async function triggerImmediateSync(): Promise<SyncResult> {
  console.log('[BackgroundSync] Triggering immediate sync...');
  return syncPendingActions();
}

export default {
  syncPendingActions,
  registerBackgroundSync,
  triggerImmediateSync,
};
